import React, { useState, useEffect, useRef } from 'react';
import { Loader2, MapPin } from 'lucide-react';

// Lieux populaires (suggestions instantanées, sans appel API)
const POPULAR_PLACES = [
  {
    label: 'Aéroport Charles de Gaulle (CDG), Roissy-en-France',
    short: 'Aéroport CDG',
    city: 'Roissy-en-France',
    postcode: '95700',
    context: '95, Val-d\u2019Oise, Île-de-France',
    coordinates: [2.5479, 49.0097]
  },
  {
    label: 'Aéroport Paris-Orly, Orly',
    short: 'Aéroport Orly',
    city: 'Orly',
    postcode: '94390',
    context: '94, Val-de-Marne, Île-de-France',
    coordinates: [2.3653, 48.7233]
  },
  {
    label: 'Aéroport du Bourget, Le Bourget',
    short: 'Aéroport Le Bourget',
    city: 'Le Bourget',
    postcode: '93350',
    context: '93, Seine-Saint-Denis, Île-de-France',
    coordinates: [2.4413, 48.9694]
  },
  {
    label: 'Disneyland Paris, Chessy',
    short: 'Disneyland Paris',
    city: 'Chessy',
    postcode: '77700',
    context: '77, Seine-et-Marne, Île-de-France',
    coordinates: [2.7833, 48.8722]
  },
  {
    label: 'Paris, 75001 (centre)',
    short: 'Paris centre',
    city: 'Paris',
    postcode: '75001',
    context: '75, Paris, Île-de-France',
    coordinates: [2.3522, 48.8566]
  },
  {
    label: 'Gare du Nord, 18 Rue de Dunkerque, 75010 Paris',
    short: 'Gare du Nord',
    city: 'Paris',
    postcode: '75010',
    context: '75, Paris, Île-de-France',
    coordinates: [2.3553, 48.8809]
  },
  {
    label: 'Gare de Lyon, 20 Boulevard Diderot, 75012 Paris',
    short: 'Gare de Lyon',
    city: 'Paris',
    postcode: '75012',
    context: '75, Paris, Île-de-France',
    coordinates: [2.3737, 48.8443]
  },
  {
    label: 'Versailles, 78000',
    short: 'Versailles',
    city: 'Versailles',
    postcode: '78000',
    context: '78, Yvelines, Île-de-France',
    coordinates: [2.1301, 48.8014]
  }
];

/**
 * Autocomplétion d'adresses via l'API Adresse (Base Adresse Nationale française)
 * + suggestions instantanées pour les lieux populaires (CDG, Orly, Disneyland, etc.)
 */
const AddressAutocomplete = ({ value, onChange, placeholder, required, className = '' }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [showPopular, setShowPopular] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const suppressFetchRef = useRef(false);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setShowPopular(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const fetchSuggestions = (q) => {
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=6&autocomplete=1`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        const items = (data.features || []).map((f) => ({
          label: f.properties.label,
          city: f.properties.city,
          postcode: f.properties.postcode,
          context: f.properties.context,
          coordinates: f.geometry?.coordinates
        }));
        setSuggestions(items);
        setOpen(items.length > 0);
        setShowPopular(false);
        setActiveIndex(-1);
      })
      .catch(() => {
        setSuggestions([]);
        setOpen(false);
      })
      .finally(() => setLoading(false));
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    if (suppressFetchRef.current) {
      suppressFetchRef.current = false;
      return;
    }
    if (!v) {
      setSuggestions([]);
      setShowPopular(true);
      setOpen(true);
      return;
    }
    setShowPopular(false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 220);
  };

  const select = (item) => {
    suppressFetchRef.current = true;
    setQuery(item.label);
    onChange(item.label, item);
    setSuggestions([]);
    setOpen(false);
    setShowPopular(false);
    setActiveIndex(-1);
  };

  const handleKey = (e) => {
    if (!open || (suggestions.length === 0 && !showPopular)) return;
    const list = showPopular ? POPULAR_PLACES : suggestions;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      select(list[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      setShowPopular(false);
    }
  };

  const handleFocus = () => {
    if (!query) {
      setShowPopular(true);
      setOpen(true);
    } else if (suggestions.length > 0) {
      setOpen(true);
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <input
        type="text"
        value={query}
        onChange={handleChange}
        onKeyDown={handleKey}
        onFocus={handleFocus}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        className={`w-full bg-transparent outline-none text-base placeholder-black/40 ${className}`}
      />
      {loading && (
        <Loader2 className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-black/40" />
      )}

      {open && showPopular && (
        <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-black/15 shadow-2xl z-50">
          <div className="px-4 py-2 text-[10px] tracking-[0.25em] uppercase text-black/50 border-b border-black/5 bg-[#faf8f5]">
            Lieux populaires
          </div>
          <ul className="max-h-72 overflow-y-auto">
            {POPULAR_PLACES.map((p, i) => (
              <li
                key={p.label}
                onMouseDown={(e) => { e.preventDefault(); select(p); }}
                onMouseEnter={() => setActiveIndex(i)}
                className={`px-4 py-3 cursor-pointer text-sm border-b border-black/5 last:border-b-0 flex items-start gap-3 ${activeIndex === i ? 'bg-black text-white' : 'hover:bg-black/5'}`}
              >
                <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${activeIndex === i ? 'text-white' : 'text-black/50'}`} />
                <div>
                  <div className="font-medium leading-tight">{p.short}</div>
                  <div className={`text-xs mt-0.5 ${activeIndex === i ? 'text-white/70' : 'text-black/50'}`}>
                    {p.label.replace(p.short + ', ', '').replace(p.short, '')}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {open && !showPopular && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full mt-2 bg-white border border-black/15 shadow-2xl max-h-72 overflow-y-auto z-50">
          {suggestions.map((s, i) => (
            <li
              key={`${s.label}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); select(s); }}
              onMouseEnter={() => setActiveIndex(i)}
              className={`px-4 py-3 cursor-pointer text-sm border-b border-black/5 last:border-b-0 ${activeIndex === i ? 'bg-black text-white' : 'hover:bg-black/5'}`}
            >
              <div className="font-medium leading-tight">{s.label}</div>
              {s.context && (
                <div className={`text-xs mt-0.5 ${activeIndex === i ? 'text-white/70' : 'text-black/50'}`}>
                  {s.context}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;

